<script lang="ts">
	import { Checkbox, Chevron, ChevronDirection } from '@repo/commons/components'

	interface Props {
		key?: string
		values?: string[]
		counts?: number[]
		booleanFilter?: boolean
		checkboxStatesWritable: Record<string, Record<string, boolean>>
		allowReordering?: boolean
		onfilterChanged?: ((key: string, value: string, checked: boolean) => void) | null
		onreorder?: ((key: string, newValue: string[]) => void) | null
	}

	let {
		key = '',
		values = $bindable([]),
		counts = [],
		booleanFilter = false,
		checkboxStatesWritable,
		allowReordering = false,
		onfilterChanged = null,
		onreorder = null
	}: Props = $props()

	let showMore: boolean = $state(false)
	const MAX_VISIBLE = 2

	// Handle checkbox change
	function handleCheckboxChange(checked: boolean, id: number): void {
		const value = booleanFilter ? key : values[id]

		checkboxStatesWritable[key] ??= {}
		checkboxStatesWritable[key][value] = checked

		onfilterChanged?.(key, value, checked)
	}

	function moveItemUp(index: number) {
		if (index > 0) {
			const newValues = [...values]
			;[newValues[index - 1], newValues[index]] = [newValues[index], newValues[index - 1]]
			values = newValues
			onreorder?.(key, newValues)
		}
	}

	function moveItemDown(index: number) {
		if (index < values.length - 1) {
			const newValues = [...values]
			;[newValues[index + 1], newValues[index]] = [newValues[index], newValues[index + 1]]
			values = newValues
			onreorder?.(key, newValues)
		}
	}
</script>

{#if booleanFilter}
	<div class="checkbox-row">
		<Checkbox label={key} onchange={handleCheckboxChange} />
	</div>
{:else}
	<div class="checkbox-container">
		{#each values.slice(0, showMore ? values.length : MAX_VISIBLE) as value, index (value)}
			<div class="checkbox-row">
				{#if allowReordering}
					<div class="chevron-icons">
						<Chevron
							direction={ChevronDirection.UP}
							class="chevron-icon"
							onclick={() => moveItemUp(index)}
						/>
						<Chevron
							direction={ChevronDirection.DOWN}
							class="chevron-icon"
							onclick={() => moveItemDown(index)}
						/>
					</div>
				{/if}
				<div class="checkbox-label-container">
					<div class="checkbox-wrapper">
						<Checkbox
							id={index.toString()}
							label={value}
							checked={checkboxStatesWritable[key]?.[value] || false}
							onchange={handleCheckboxChange}
						/>
					</div>
				</div>
				<span class="count">{counts[index] ? counts[index] : ''}</span>
			</div>
		{/each}
	</div>

	{#if values.length > MAX_VISIBLE}
		<div class="show-more-toggle" onclick={() => (showMore = !showMore)}>
			{showMore ? 'show less' : 'show more'}
			<Chevron direction={showMore ? ChevronDirection.UP : ChevronDirection.DOWN} />
		</div>
	{/if}
{/if}

<style>
	.checkbox-container {
		@apply flex flex-col;
	}

	.checkbox-row {
		@apply flex items-center justify-between py-2;
		border-bottom: 1px solid #e2e2e2;
	}

	.checkbox-label-container {
		@apply flex-1 flex items-center;
	}

	.checkbox-wrapper {
		@apply flex items-center;
		min-width: 150px;
	}

	.chevron-icons {
		@apply flex flex-col mr-2;
	}

	.chevron-icon {
		@apply cursor-pointer;
	}

	.count {
		@apply text-gray-600 ml-4;
	}

	.show-more-toggle {
		@apply text-blue-500 cursor-pointer mt-2 flex items-center;
	}
</style>
